import {
  Boxes, Building, ShoppingBag, Layout, Headphones, Globe,
  UserCheck, FileText, Briefcase, DollarSign, ShieldCheck,
  GraduationCap, BookOpen, Stethoscope, Truck, Package,
  CreditCard, Users, Layers, type LucideIcon,
} from "lucide-react";

export type CatalogItem = { slug: string; title: string; desc: string; icon: LucideIcon };
export type CatalogGroup = { title: string; items: CatalogItem[] };
export type CatalogCategory = {
  slug: string;
  title: string;
  tagline: string;
  icon: LucideIcon;
  intro: string;
  groups: CatalogGroup[];
};

export const CATALOG: Record<string, CatalogCategory> = {
  "pos-software": {
    slug: "pos-software",
    title: "POS Software",
    tagline: "Retail & restaurant point of sale.",
    icon: CreditCard,
    intro:
      "Modern point-of-sale systems for retail stores, restaurants, and multi-branch operations — fast checkout, real-time inventory, kitchen displays and online orders in one connected platform.",
    groups: [
      { title: "Retail POS", items: [
        { slug: "single-store-pos", title: "Single-store POS", desc: "Fast, offline-capable checkout for one location.", icon: ShoppingBag },
        { slug: "multi-branch-pos", title: "Multi-branch POS", desc: "Centralized inventory & reporting across branches.", icon: Building },
        { slug: "barcode-inventory", title: "Barcode & Inventory", desc: "Barcode scanning, stock counts, low-stock alerts.", icon: Boxes },
      ]},
      { title: "Restaurant POS", items: [
        { slug: "table-management", title: "Table Management", desc: "Floor plans, table status, split bills, tips.", icon: Layout },
        { slug: "kitchen-display", title: "Kitchen Display", desc: "KDS screens that route orders in real time.", icon: Headphones },
        { slug: "online-orders", title: "Online Orders", desc: "Direct online ordering with delivery integration.", icon: Globe },
      ]},
    ],
  },
  "hrm-payroll": {
    slug: "hrm-payroll",
    title: "HRM & Payroll",
    tagline: "People operations, hiring to payroll.",
    icon: Users,
    intro:
      "Everything HR — from applicant tracking and employee records to attendance, leave, payroll and tax compliance — in one platform your team will actually use.",
    groups: [
      { title: "HR", items: [
        { slug: "employee-records", title: "Employee Records", desc: "Digital HR files, documents, and org chart.", icon: UserCheck },
        { slug: "attendance-leave", title: "Attendance & Leave", desc: "Time tracking, shifts, and leave workflows.", icon: FileText },
        { slug: "recruitment", title: "Recruitment", desc: "ATS with pipelines, interviews and offers.", icon: Briefcase },
      ]},
      { title: "Payroll", items: [
        { slug: "payroll-processing", title: "Payroll Processing", desc: "Multi-country payroll runs and payslips.", icon: DollarSign },
        { slug: "tax-compliance", title: "Tax Compliance", desc: "Local tax filings, deductions and audit trail.", icon: ShieldCheck },
      ]},
    ],
  },
  "industry-solutions": {
    slug: "industry-solutions",
    title: "Industry Solutions",
    tagline: "Pre-built systems for vertical industries.",
    icon: Layers,
    intro:
      "Tailored software for education, healthcare, and logistics — production-tested modules you can deploy in weeks, not quarters.",
    groups: [
      { title: "Education", items: [
        { slug: "school-management", title: "School Management", desc: "Admissions, timetable, grading and parents.", icon: GraduationCap },
        { slug: "lms-platform", title: "LMS Platform", desc: "Courses, assignments, quizzes and certificates.", icon: BookOpen },
      ]},
      { title: "Healthcare", items: [
        { slug: "hospital-management", title: "Hospital Management", desc: "Patients, appointments, EMR and billing.", icon: Stethoscope },
        { slug: "clinic-pharmacy", title: "Clinic & Pharmacy", desc: "Prescriptions, inventory and insurance.", icon: ShieldCheck },
      ]},
      { title: "Logistics", items: [
        { slug: "fleet-management", title: "Fleet Management", desc: "Vehicles, drivers, routes and telemetry.", icon: Truck },
        { slug: "warehouse-management", title: "Warehouse Management", desc: "Bins, picking, packing and shipping.", icon: Package },
      ]},
    ],
  },
};

export function getCatalogCategory(slug: string): CatalogCategory | undefined {
  return CATALOG[slug];
}

export function getCatalogChild(catSlug: string, childSlug: string): { cat: CatalogCategory; item: CatalogItem } | undefined {
  const cat = CATALOG[catSlug];
  if (!cat) return undefined;
  for (const g of cat.groups) {
    const item = g.items.find((i) => i.slug === childSlug);
    if (item) return { cat, item };
  }
  return undefined;
}
